import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileDown, RefreshCw, Search, Tag, TrendingDown, TrendingUp, Users, Wallet, Award } from "lucide-react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { fmtBDT } from "../lib/format";
import AffiliateProgram from "./AffiliateProgram";
import usePagedRows from "../components/usePagedRows";
import Pagination from "../components/Pagination";
import "../admin-affiliate-premium.css";

/* ============================================================================
 * Affiliate History (admin) — per-affiliate × per-referred-user activity:
 *   who registered under which affiliate code/promo, deposits, withdrawals,
 *   net loss/win, commission earned, paid out. Includes PDF export.
 * ============================================================================ */
function HistoryPage() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [periodDays, setPeriodDays] = useState(0); // 0 = lifetime
  const [view, setView] = useState("detail"); // "detail" | "summary"
  const [downloading, setDownloading] = useState(false);
  const paged = usePagedRows(rows, 12, [search, periodDays, view]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/affiliate-history-enriched", {
        params: { period_days: periodDays, search: search || undefined, view },
      });
      setRows(data.items || []);
      setTotals(data.totals || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load affiliate history");
    } finally {
      setLoading(false);
    }
  }, [periodDays, search, view]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/admin/affiliate-history.pdf", {
        params: { period_days: periodDays, search: search || undefined },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const tag = periodDays > 0 ? `last-${periodDays}d` : "lifetime";
      a.download = `affiliate-history-${tag}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${a.download}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF download failed");
    } finally {
      setDownloading(false);
    }
  };

  const periods = [
    { v: 0, label: "Lifetime" },
    { v: 7, label: "Last 7d" },
    { v: 15, label: "Last 15d" },
    { v: 30, label: "Last 30d" },
    { v: 90, label: "Last 90d" },
  ];

  return (
    <div data-testid="affiliate-history-page" className="aff-premium">
      <PageHeader
        title="Affiliate History"
        subtitle={view === "summary"
          ? "Per affiliate · totals across all their referred users (one row per affiliate)"
          : "Per-affiliate × per-referred-user — who registered with which code / promo, deposits, withdrawals, net loss/win, commission earned, paid out"}
        actions={
          <button
            onClick={downloadPdf}
            disabled={downloading || loading}
            className="h-9 px-3 inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white rounded-sm text-[13px] font-semibold disabled:opacity-50"
            data-testid="affiliate-history-export-pdf"
            title="Download affiliate history as PDF"
          >
            <FileDown size={13} /> {downloading ? "Generating…" : "Export PDF"}
          </button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <StatCard label={view === "summary" ? "Affiliates" : "Rows"} value={totals.rows || 0} color="blue" icon={Users} />
        <StatCard label="Deposits" value={fmtBDT(totals.deposits || 0)} color="emerald" icon={Wallet} />
        <StatCard label="Withdrawals" value={fmtBDT(totals.withdrawals || 0)} color="amber" icon={Wallet} />
        <StatCard label="Net Loss" value={fmtBDT(totals.net_loss || 0)} color="rose" icon={TrendingDown} />
        <StatCard label="Commission Earned" value={fmtBDT(totals.commission_earned || 0)} color="violet" icon={Award} />
      </div>

      <div className="bg-white border border-gray-200 rounded-sm p-3 flex flex-wrap items-center gap-2 mb-4">
        {/* View toggle — Per User vs Per Affiliate */}
        <div className="inline-flex bg-gray-100 rounded-sm p-0.5" data-testid="aff-hist-view-toggle">
          <button
            onClick={() => setView("detail")}
            className={`px-3 h-7 text-[11px] font-semibold rounded-sm transition ${
              view === "detail" ? "bg-white text-violet-700 shadow-sm" : "text-gray-500 hover:text-gray-800"
            }`}
            data-testid="aff-hist-view-detail"
          >Per Referred User</button>
          <button
            onClick={() => setView("summary")}
            className={`px-3 h-7 text-[11px] font-semibold rounded-sm transition ${
              view === "summary" ? "bg-white text-violet-700 shadow-sm" : "text-gray-500 hover:text-gray-800"
            }`}
            data-testid="aff-hist-view-summary"
          >Per Affiliate</button>
        </div>
        <span className="hidden md:inline-block w-px h-5 bg-gray-200 mx-1" />
        <span className="text-sm text-gray-700 font-semibold">Period:</span>
        {periods.map((p) => (
          <button
            key={p.v}
            onClick={() => setPeriodDays(p.v)}
            className={`px-3 h-8 text-xs font-semibold rounded-sm ${
              periodDays === p.v ? "bg-violet-600 text-white" : "bg-gray-50 text-gray-600 hover:bg-gray-100"
            }`}
            data-testid={`aff-hist-period-${p.v}`}
          >
            {p.label}
          </button>
        ))}
        <div className="relative flex-1 min-w-[260px] ml-auto">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by affiliate, code, promo, username, phone..."
            className="w-full h-8 pl-8 pr-3 border border-gray-300 rounded-sm text-sm"
            data-testid="aff-history-search"
          />
        </div>
        <button onClick={load} className="h-8 px-3 border border-gray-300 rounded-sm text-sm inline-flex items-center gap-1.5 hover:bg-gray-50">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {view === "summary" ? (
        <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto" data-testid="aff-history-table-summary">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Affiliate</th>
                <th className="px-3 py-2 text-left font-semibold">Code / Promo</th>
                <th className="px-3 py-2 text-center font-semibold">Referred Users</th>
                <th className="px-3 py-2 text-right font-semibold">Deposits</th>
                <th className="px-3 py-2 text-right font-semibold">Withdrawals</th>
                <th className="px-3 py-2 text-right font-semibold">Net Loss / Win</th>
                <th className="px-3 py-2 text-center font-semibold">Rate</th>
                <th className="px-3 py-2 text-right font-semibold text-emerald-700">Commission</th>
                <th className="px-3 py-2 text-right font-semibold text-violet-700">Paid Out</th>
                <th className="px-3 py-2 text-center font-semibold">Active / Blocked</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {!loading && rows.length === 0 && (
                <tr><td colSpan={10} className="px-3 py-12 text-center text-gray-400">No affiliates yet.</td></tr>
              )}
              {paged.visible.map((r, idx) => {
                const net = r.net_diff;
                const isWin = net < 0;
                return (
                  <tr key={`${r.affiliate_id}-${idx}`} className="hover:bg-gray-50" data-testid={`aff-hist-sum-row-${idx}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">{r.affiliate_username || "—"}</div>
                      {r.tier_name && <div className="text-[10px] text-violet-600 font-bold uppercase">{r.tier_name}</div>}
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-gray-700">
                      {r.refer_code || "—"}
                      {r.promo_code && (
                        <div className="mt-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 border border-emerald-300 text-emerald-700 rounded-sm font-bold text-[10px]">
                          <Tag size={9} /> {r.promo_code}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className="inline-flex items-center justify-center min-w-[32px] h-6 px-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-sm font-bold text-[12px] tabular-nums">
                        {r.referred_users || 0}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-blue-700">{fmtBDT(r.deposits)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-amber-700">{fmtBDT(r.withdrawals)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-semibold ${isWin ? "text-emerald-700" : "text-rose-700"}`}>
                      {isWin ? <span className="inline-flex items-center gap-1"><TrendingUp size={11} /> -{fmtBDT(Math.abs(net))}</span>
                             : fmtBDT(net)}
                      <div className={`text-[10px] uppercase tracking-wider font-bold ${isWin ? "text-emerald-600" : "text-rose-600"}`}>
                        {isWin ? "Win" : "Loss"}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center text-[12px] font-bold">{Number(r.commission_rate || 0).toFixed(1)}%</td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-700 font-bold">{fmtBDT(r.commission_earned)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-violet-700">{fmtBDT(r.paid_out)}</td>
                    <td className="px-3 py-2 text-center">
                      <div className="inline-flex items-center gap-1 text-[11px]">
                        <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm font-semibold">{r.active_users || 0}</span>
                        <span className="text-gray-300">/</span>
                        <span className="px-1.5 py-0.5 bg-rose-50 text-rose-700 border border-rose-200 rounded-sm font-semibold">{r.blocked_users || 0}</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
      <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto" data-testid="aff-history-table">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Affiliate</th>
              <th className="px-3 py-2 text-left font-semibold">Code / Promo</th>
              <th className="px-3 py-2 text-left font-semibold">Referred User</th>
              <th className="px-3 py-2 text-left font-semibold">Phone</th>
              <th className="px-3 py-2 text-left font-semibold">Code Used</th>
              <th className="px-3 py-2 text-left font-semibold">Joined</th>
              <th className="px-3 py-2 text-right font-semibold">Deposits</th>
              <th className="px-3 py-2 text-right font-semibold">Withdrawals</th>
              <th className="px-3 py-2 text-right font-semibold">Net Loss / Win</th>
              <th className="px-3 py-2 text-center font-semibold">Rate</th>
              <th className="px-3 py-2 text-right font-semibold text-emerald-700">Commission</th>
              <th className="px-3 py-2 text-right font-semibold text-violet-700">Paid Out</th>
              <th className="px-3 py-2 text-center font-semibold">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {!loading && rows.length === 0 && (
              <tr><td colSpan={13} className="px-3 py-12 text-center text-gray-400">No records yet.</td></tr>
            )}
            {paged.visible.map((r, idx) => {
              const net = r.net_diff;
              const isWin = net < 0;
              return (
                <tr key={`${r.affiliate_id}-${r.user_id}-${idx}`} className="hover:bg-gray-50" data-testid={`aff-hist-row-${idx}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-900">{r.affiliate_username || "—"}</div>
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] text-gray-700">
                    {r.refer_code || "—"}
                    {r.promo_code && (
                      <div className="mt-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 border border-emerald-300 text-emerald-700 rounded-sm font-bold text-[10px]">
                        <Tag size={9} /> {r.promo_code}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-800">{r.user_username || "—"}</div>
                    {r.user_email && <div className="text-[11px] text-gray-500">{r.user_email}</div>}
                  </td>
                  <td className="px-3 py-2 text-[12px] text-gray-600">{r.user_phone || "—"}</td>
                  <td className="px-3 py-2 font-mono text-[11px] text-indigo-700">{r.promo_code_used || "—"}</td>
                  <td className="px-3 py-2 text-[11px] text-gray-500 whitespace-nowrap">
                    {r.joined_at ? new Date(r.joined_at).toLocaleDateString("en-GB") : "—"}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-blue-700">{fmtBDT(r.deposits)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amber-700">{fmtBDT(r.withdrawals)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums font-semibold ${isWin ? "text-emerald-700" : "text-rose-700"}`}>
                    {isWin ? <span className="inline-flex items-center gap-1"><TrendingUp size={11} /> -{fmtBDT(Math.abs(net))}</span>
                           : fmtBDT(net)}
                    <div className={`text-[10px] uppercase tracking-wider font-bold ${isWin ? "text-emerald-600" : "text-rose-600"}`}>
                      {isWin ? "Win" : "Loss"}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center text-[12px] font-bold">{Number(r.commission_rate || 0).toFixed(1)}%</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700 font-bold">{fmtBDT(r.commission_earned)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-violet-700">{fmtBDT(r.paid_out)}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`inline-flex px-2 py-0.5 rounded-sm font-semibold text-[10px] uppercase tracking-wider border ${
                      r.status === "Blocked"
                        ? "bg-rose-50 text-rose-700 border-rose-200"
                        : "bg-emerald-50 text-emerald-700 border-emerald-200"
                    }`}>
                      {r.status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
      <Pagination total={paged.totalRows} page={paged.page} pageSize={paged.pageSize} onChange={paged.setPage} testIdPrefix="aff-history-pg" />
    </div>
  );
}

/* ============================================================================
 * Affiliate Transfers (admin) — actual paid commission payouts ledger
 * (sourced from /affiliate-commissions where status=paid).
 * ============================================================================ */
function TransferPage() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [downloading, setDownloading] = useState(false);
  const paged = usePagedRows(rows, 12, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/affiliate-transfers-enriched", {
        params: { search: search || undefined },
      });
      setRows(data.items || []);
      setTotals(data.totals || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load transfers");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/admin/affiliate-transfers.pdf", {
        params: { search: search || undefined },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `affiliate-transfers.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${a.download}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-testid="affiliate-transfer-page" className="aff-premium">
      <PageHeader
        title="Affiliate Transfers"
        subtitle="All completed commission payouts to affiliate wallets — period, deposits, net loss, rate, and amount paid"
        actions={
          <button
            onClick={downloadPdf}
            disabled={downloading || loading}
            className="h-9 px-3 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-[13px] font-semibold disabled:opacity-50"
            data-testid="affiliate-transfer-export-pdf"
            title="Download transfers as PDF"
          >
            <FileDown size={13} /> {downloading ? "Generating…" : "Export PDF"}
          </button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard label="Total Transfers" value={totals.rows || 0} color="blue" icon={Wallet} />
        <StatCard label="Total Paid Out" value={fmtBDT(totals.amount || 0)} color="emerald" icon={Award} />
        <StatCard label="Net Loss Covered" value={fmtBDT(totals.net_loss || 0)} color="rose" icon={TrendingDown} />
        <StatCard label="Deposits in Periods" value={fmtBDT(totals.deposits || 0)} color="violet" icon={Wallet} />
      </div>

      <div className="bg-white border border-gray-200 rounded-sm p-3 flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[260px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search affiliate, code, promo, downline..."
            className="w-full h-8 pl-8 pr-3 border border-gray-300 rounded-sm text-sm"
            data-testid="aff-transfer-search"
          />
        </div>
        <button onClick={load} className="h-8 px-3 border border-gray-300 rounded-sm text-sm inline-flex items-center gap-1.5 hover:bg-gray-50">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto" data-testid="aff-transfer-table">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Affiliate</th>
              <th className="px-3 py-2 text-left font-semibold">Code / Promo</th>
              <th className="px-3 py-2 text-center font-semibold">Type</th>
              <th className="px-3 py-2 text-left font-semibold">Period</th>
              <th className="px-3 py-2 text-right font-semibold">Refs</th>
              <th className="px-3 py-2 text-right font-semibold">Deposits</th>
              <th className="px-3 py-2 text-right font-semibold">Net Loss</th>
              <th className="px-3 py-2 text-center font-semibold">Rate</th>
              <th className="px-3 py-2 text-right font-semibold text-emerald-700">Amount</th>
              <th className="px-3 py-2 text-center font-semibold">Status</th>
              <th className="px-3 py-2 text-left font-semibold">Paid At</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {!loading && rows.length === 0 && (
              <tr><td colSpan={11} className="px-3 py-12 text-center text-gray-400">No paid transfers yet.</td></tr>
            )}
            {paged.visible.map((r, idx) => {
              const isSub = r.type === "sub_override";
              return (
                <tr key={r.id || idx} className="hover:bg-gray-50" data-testid={`aff-transfer-row-${idx}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-900">{r.affiliate_username || "—"}</div>
                    {isSub && r.downline_username && (
                      <div className="text-[10px] text-purple-600 mt-0.5">↳ from <b>{r.downline_username}</b></div>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] text-gray-700">
                    {r.refer_code || "—"}
                    {r.promo_code && (
                      <div className="mt-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 border border-emerald-300 text-emerald-700 rounded-sm font-bold text-[10px]">
                        <Tag size={9} /> {r.promo_code}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={`inline-flex px-2 py-0.5 rounded-sm font-bold text-[10px] uppercase tracking-wider border ${
                      isSub
                        ? "bg-purple-50 text-purple-700 border-purple-200"
                        : "bg-emerald-50 text-emerald-700 border-emerald-200"
                    }`}>
                      {isSub ? "Sub-Override" : "RevShare"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[11px] text-gray-600 whitespace-nowrap">
                    {(r.period_start || "").slice(0, 10)} → {(r.period_end || "").slice(0, 10)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.referred_count || 0}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-blue-700">{fmtBDT(r.total_deposits)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-rose-700">{fmtBDT(r.net_loss)}</td>
                  <td className="px-3 py-2 text-center text-[12px] font-bold">{Number(r.commission_rate || 0).toFixed(1)}%</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700 font-bold">{fmtBDT(r.amount)}</td>
                  <td className="px-3 py-2 text-center">
                    <span className="inline-flex px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm font-semibold text-[10px] uppercase tracking-wider">
                      {r.status || "completed"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[11px] text-gray-600 whitespace-nowrap">
                    {r.paid_at ? new Date(r.paid_at).toLocaleString("en-GB") : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pagination total={paged.totalRows} page={paged.page} pageSize={paged.pageSize} onChange={paged.setPage} testIdPrefix="aff-transfer-pg" />
    </div>
  );
}

/* Small reusable stat card matching the program tabs' style */
function StatCard({ label, value, color = "blue", icon: Icon }) {
  const palette = {
    blue: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", icon: "text-blue-500" },
    emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", icon: "text-emerald-500" },
    rose: { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", icon: "text-rose-500" },
    amber: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", icon: "text-amber-500" },
    violet: { bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-700", icon: "text-violet-500" },
  }[color] || {};
  return (
    <div className={`rounded-sm border ${palette.border} ${palette.bg} px-4 py-3 flex items-center justify-between`}>
      <div className="min-w-0">
        <div className={`text-[10px] uppercase tracking-wider font-semibold ${palette.text} opacity-80`}>{label}</div>
        <div className={`text-lg font-bold ${palette.text} mt-0.5 truncate`}>{value}</div>
      </div>
      {Icon && <Icon size={18} className={palette.icon} />}
    </div>
  );
}

export default function Affiliate({ variant = "list" }) {
  // "list" variant uses the full Affiliate Program management UI
  if (variant === "list") {
    return <AffiliateProgram defaultTab="affiliates" />;
  }
  if (variant === "history") return <HistoryPage />;
  if (variant === "transfer") return <TransferPage />;
  return <HistoryPage />;
}
